import React, { useState, useEffect } from 'react';
import { Table, InputNumber, Button, message } from 'antd';
import axios from 'axios';

function GeneralWeightingMethod({ fileId, fileName, weightingConfig, setSelectedFile }) {
  const [factors, setFactors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [scores, setScores] = useState({});
  const [result, setResult] = useState(null);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (fileId) {
      fetchData();
    } else if (weightingConfig) {
      initializeWeighting(weightingConfig);
    }

  }, [fileId, weightingConfig]);

  // Fetch saved analysis from backend
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        message.error("User is not authenticated.");
        return;
      }

      const response = await axios.get(`/api/retrieve_general_weighting_method/${fileId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      setFactors(data.input_data.factors);
      const locationNames = Object.keys(data.input_data.locations);
      setLocations(locationNames);
      setScores(data.input_data.locations);
      setResult(data.output_data);
      setIsSaved(true);

    } catch (error) {
      console.error('Error fetching weighting method data:', error);
      message.error('Failed to load General Weighting Method data');
    }
  };

  // Initialize a new (unsaved) analysis from the setup modal's config
  const initializeWeighting = (config) => {
    const factorNames = config.rowNames || config.factorNames || Array.from({ length: config.numFactors || 3 }, (_, i) => `Factor ${i + 1}`);
    const locationNames = config.colNames || config.locationNames || Array.from({ length: config.numLocations || 2 }, (_, i) => `Location ${i + 1}`);

    const initialWeight = factorNames.length ? 1 / factorNames.length : 0;
    const initialFactors = factorNames.map((name) => ({ name, weight: initialWeight }));

    const initialScores = {};
    locationNames.forEach((loc) => {
      initialScores[loc] = {};
      factorNames.forEach((f) => {
        initialScores[loc][f] = 0;
      });
    });

    setFactors(initialFactors);
    setLocations(locationNames);
    setScores(initialScores);
  };

  const updateWeight = (factorIndex, value) => {
    const newFactors = [...factors];
    newFactors[factorIndex] = { ...newFactors[factorIndex], weight: value };
    setFactors(newFactors);
  };

  const updateScore = (locationName, factorName, value) => {
    setScores((prev) => ({
      ...prev,
      [locationName]: { ...prev[locationName], [factorName]: value },
    }));
  };

  // Save or update the analysis
  const handleSave = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        message.error("User is not authenticated.");
        return;
      }

      const payload = {
        name: fileName,
        input_data: {
          factors,
          locations: scores,
        },
      };

      const url = fileId
        ? `/api/update_general_weighting_method/${fileId}/`
        : '/api/save_general_weighting_method/';

      const method = fileId ? 'put' : 'post';

      const response = await axios({
        method,
        url,
        data: payload,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!fileId && response.data.id) {
        if (typeof setSelectedFile === "function") {
          setSelectedFile(prev => ({
            ...prev,
            id: response.data.id,
            type: "weighting_method"
          }));
        } else {
          console.error("setSelectedFile is undefined! Check if it's passed as a prop.");
        }
      }

      setResult(response.data.output_data);
      setIsSaved(true);
      message.success('General Weighting Method analysis saved successfully');

    } catch (error) {
      console.error('Error saving weighting method:', error);
      message.error('Failed to save General Weighting Method analysis');
    }
  };

  if (!factors.length) return <p>Loading...</p>;

  const totalWeight = factors.reduce((sum, f) => sum + (Number(f.weight) || 0), 0);

  return (
    <div>
      <h3>{fileName} - General Weighting Method</h3>
      <Table
        bordered
        pagination={false}
        columns={[
          { title: 'Factor', dataIndex: 'factor', key: 'factor' },
          { title: 'Weight', dataIndex: 'weight', key: 'weight' },
          ...locations.map((loc) => ({
            title: loc,
            dataIndex: loc,
            key: loc,
          })),
        ]}
        dataSource={factors.map((f, factorIndex) => {
          let row = {
            key: factorIndex,
            factor: f.name,
            weight: (
              <InputNumber
                min={0}
                step={0.01}
                value={f.weight}
                onChange={(value) => updateWeight(factorIndex, value)}
              />
            ),
          };
          locations.forEach((loc) => {
            row[loc] = (
              <InputNumber
                min={0}
                value={scores[loc]?.[f.name] ?? 0}
                onChange={(value) => updateScore(loc, f.name, value)}
              />
            );
          });
          return row;
        })}
      />
      <p style={{ marginTop: 8, color: Math.abs(totalWeight - 1) < 0.005 ? 'green' : '#d48806' }}>
        Total weight: {totalWeight.toFixed(2)} {Math.abs(totalWeight - 1) < 0.005 ? '✓' : '(should equal 1.00)'}
      </p>

      <Button type="primary" onClick={handleSave} style={{ marginTop: '16px' }}>
        {isSaved ? 'Update' : 'Save'} Analysis
      </Button>

      {result && (
        <div style={{ marginTop: '24px' }}>
          <h4>Results</h4>
          <Table
            bordered
            pagination={false}
            columns={[
              { title: 'Location', dataIndex: 'location', key: 'location' },
              { title: 'Weighted Total', dataIndex: 'total', key: 'total' },
              { title: 'Weighted Average', dataIndex: 'average', key: 'average' },
            ]}
            dataSource={locations.map((loc) => ({
              key: loc,
              location: loc + (loc === result.best_location ? ' 🏆' : ''),
              total: result.weighted_totals[loc]?.toFixed(2),
              average: result.weighted_averages[loc]?.toFixed(2),
            }))}
          />

          <h4 style={{ marginTop: 16 }}>Multiplications</h4>
          <Table
            bordered
            pagination={false}
            columns={[
              { title: 'Factor', dataIndex: 'factor', key: 'factor' },
              { title: 'Weight', dataIndex: 'weight', key: 'weight' },
              ...locations.map((loc) => ({ title: loc, dataIndex: loc, key: loc })),
            ]}
            dataSource={factors.map((f, i) => {
              let row = { key: i, factor: f.name, weight: f.weight };
              locations.forEach((loc) => {
                row[loc] = result.multiplications[loc]?.[f.name]?.toFixed(2);
              });
              return row;
            })}
          />

          <p style={{ marginTop: 12 }}>
            Best location based on highest Weighted Total: <strong>{result.best_location}</strong>
          </p>
        </div>
      )}
    </div>
  );
}

export default GeneralWeightingMethod;